/** Globaler Draft-Store fuer minimierte Modals (Kalender-Termin, Kontakt-Anlage,
 *  neue E-Mail).
 *  Persistiert in localStorage → Draft bleibt beim Navigieren zwischen Seiten erhalten.
 *  Event-basiert damit AppLayout live re-rendert wenn ein Draft hinzukommt.
 */

export type DraftKind = "calendar" | "contact" | "mail"

export type CalendarDraft = {
  kind: "calendar"
  id: string  // uuid
  created_at: number
  tenant: string
  title: string
  start: string
  end: string
  allDay?: boolean
  categoryId?: string
  partnerIds?: number[]
  description?: string
  projectId?: string
}

export type ContactDraft = {
  kind: "contact"
  id: string
  created_at: number
  source: "email" | "sipgate" | "meiti"
  sourceId: number | string  // email_id or call_id
  tenantSlug?: string
  // Formular-Felder (werden beim Restore wieder eingefuellt)
  first_name?: string
  last_name?: string
  /** Firmenname — bei reinen Firmenkontakten der einzige Name (#49). */
  company_name?: string
  /** Art des Kontakts: customer | supplier | partner | other (#50). */
  category?: string
  email?: string
  phone?: string
  phone_mobile?: string
  street?: string
  zipcode?: string
  city?: string
  salutation?: string
  // Projekt-Block
  withProject?: boolean
  projectOnly?: boolean
  projectName?: string
  projectDescription?: string
}

/** Angefangene „Neue E-Mail" (#131, Marlene 24.09.2026: „Email muss als
 *  Entwurf gespeichert bleiben"). Anhaenge sind Datei-Objekte und lassen sich
 *  nicht in localStorage ablegen — sie gehen beim Schliessen verloren, der
 *  Dialog sagt das beim Speichern dazu. */
export type MailDraft = {
  kind: "mail"
  id: string
  created_at: number
  tenant: string
  to: string
  cc: string
  subject: string
  body: string
}

export type AnyDraft = CalendarDraft | ContactDraft | MailDraft

const STORAGE_KEY = "draft_modals_v1"
const EVENT_NAME = "draft-modals-changed"

function read(): AnyDraft[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw)
    return Array.isArray(arr) ? arr : []
  } catch { return [] }
}

function write(drafts: AnyDraft[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts))
    // Gleiche-Tab-Komponenten benachrichtigen (storage-Event feuert nur cross-tab)
    window.dispatchEvent(new CustomEvent(EVENT_NAME))
  } catch { /* quota exceeded → silent */ }
}

export function listDrafts(): AnyDraft[] {
  return read().sort((a, b) => b.created_at - a.created_at)
}

export function saveDraft(draft: AnyDraft) {
  const all = read().filter((d) => d.id !== draft.id)
  all.push(draft)
  write(all)
}

export function removeDraft(id: string) {
  write(read().filter((d) => d.id !== id))
}

export function getDraft(id: string): AnyDraft | undefined {
  return read().find((d) => d.id === id)
}

export function newDraftId(): string {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/** React-Hook: subscriben auf Draft-Changes (gleiche Tab + storage-event cross-tab). */
export function subscribeDrafts(cb: () => void): () => void {
  const onChange = () => cb()
  window.addEventListener(EVENT_NAME, onChange)
  window.addEventListener("storage", (e) => { if (e.key === STORAGE_KEY) onChange() })
  return () => {
    window.removeEventListener(EVENT_NAME, onChange)
    window.removeEventListener("storage", onChange as any)
  }
}
